import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import {
  ChecklistCountsEntity,
  ChecklistItemsTabStatus,
} from "../../domain/entities/ChecklistEntity";
import {
  MONTH_END_CHECKLIST_ACCOUNTING_AREAS,
  MONTH_END_CHECKLIST_ALL_AREAS_KEY,
} from "../../../../shared/constants/monthEndChecklist";

interface AccountingAreaSidebarProps {
  counts: ChecklistCountsEntity | null;
  selectedArea: string | null;
  onAreaChange: (area: string) => void;
  status: ChecklistItemsTabStatus | null;
}

const ACCOUNTING_AREAS = MONTH_END_CHECKLIST_ACCOUNTING_AREAS;

const areaRows: { key: string; label: string }[] = [
  { key: MONTH_END_CHECKLIST_ALL_AREAS_KEY, label: "All" },
  ...ACCOUNTING_AREAS.map((area) => ({ key: area, label: area })),
];

export const AccountingAreaSidebar: React.FC<AccountingAreaSidebarProps> = ({
  counts,
  selectedArea,
  onAreaChange,
  status,
}) => {
  const getCountForArea = (areaKey: string): number => {
    if (!counts || !counts[areaKey]) {
      return 0;
    }
    const areaCounts = counts[areaKey];

    if (status === "pending") {
      return areaCounts.pending || 0;
    }
    if (status === "done") {
      return areaCounts.done || 0;
    }
    if (status === "rejected") {
      return areaCounts.rejected || 0;
    }
    if (status === "to_be_done_later") {
      return areaCounts.to_be_done_later || 0;
    }

    return areaCounts.total || 0;
  };

  const badgeVariant = (): React.ComponentProps<typeof Badge>["variant"] => {
    if (status === "pending") return "error";
    if (status === "done") return "success";
    if (status === "rejected") return "warning";
    if (status === "to_be_done_later") return "neutral";
    return "neutral";
  };

  return (
    <div className="flex w-64 flex-none flex-col items-start gap-6 self-stretch border-r border-solid border-neutral-border bg-default-background px-4 py-6 overflow-auto">
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        ACCOUNTING AREAS
      </span>
      <div className="flex w-full flex-col items-start gap-2">
        {areaRows.map(({ key, label }) => {
          const count = getCountForArea(key);
          const isSelected = selectedArea === key;

          return (
            <div
              key={key}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-3 cursor-pointer ${
                isSelected ? "bg-brand-50" : ""
              }`}
              onClick={() => onAreaChange(key)}
            >
              <span
                className={`grow shrink-0 basis-0 whitespace-pre-wrap ${
                  isSelected
                    ? "text-body-bold font-body-bold text-default-font"
                    : "text-body font-body text-default-font"
                }`}
              >
                {label}
              </span>
              {count > 0 ? (
                <Badge variant={badgeVariant()}>{count}</Badge>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
